import React, { useCallback, useState, useRef } from 'react';

import DateRangeIcon from '@material-ui/icons/DateRange';
import MeshDateRangePicker from 'components/MeshDateRangePicker';
import { StyledBox, StyledButton, StyledTypography } from 'styles/common';

import { thisQuarter } from 'constants/ProjectConstants';
import { convertDateToString2 } from 'constants/helper';
import { CalendarIcon, CaretDownIcon, StyledIcon } from 'constants/icons';

const DateRangeSelectionDropdown = ({
  onDateRangeChange,
  predefinedRanges,
  defaultDateRange = thisQuarter,
  minDate,
  maxDate,
  buttonText = '',
  showDropdownArrow = true,
  popupPlacement = 'bottom-end',
  // prop to make component controlled, if this is supplied, even `null`,
  // then defaultDateRange will be ignored and this component will not manage state
  dateRange = undefined,
  clearable = false,
  secondaryActionText,
  primaryActionText,
  iconSize = 'tinyFixed',
  modifiers,
  disablePortal,
  zIndex,
  customCalendarIcon = false,
  buttonHeight = '2.5rem',
  buttonStyles = {},
  iconColor = 'primary',
  typographyColor = 'primary',
  dropdownArrowColor = 'primary',
  fontVariant = 'body1',
  buttonWidth = 'auto',
  DisplayComponent = null,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedDateRange, setDateRange] = useState(defaultDateRange);
  const displayDateRange = dateRange === undefined ? selectedDateRange : dateRange;

  const buttonRef = useRef(null);

  const toggle = useCallback(() => {
    setOpen((open) => !open);
  });

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const onSelectDateRange = useCallback((dateRange) => {
    setDateRange(dateRange);
    onDateRangeChange(dateRange);
    setOpen(false);
  }, []);

  const formattedDateRange = useCallback((dateRange) => {
    if (dateRange.label) return dateRange.label;

    if (dateRange?.startDate) {
      return `${convertDateToString2(dateRange.startDate)} - ${convertDateToString2(dateRange.endDate)}`;
    }
    return 'All';
  }, []);

  return (
    <>
      {DisplayComponent ? (
        <DisplayComponent onClick={toggle} ref={buttonRef} />
      ) : (
        <StyledBox>
          <StyledButton
            variant="outlined"
            borderColor="primary"
            ref={buttonRef}
            onClick={toggle}
            customColor="white"
            style={{
              position: 'relative',
              zIndex: 2,
              height: buttonHeight,
              width: buttonWidth,
              ...buttonStyles,
            }}
            padding="0.5rem 1rem"
          >
            <StyledIcon color={iconColor} rightGutter={1} size={iconSize}>
              {customCalendarIcon ? <CalendarIcon size={iconSize} /> : <DateRangeIcon />}
            </StyledIcon>

            <StyledTypography variant={fontVariant} color={typographyColor}>
              {buttonText || formattedDateRange(displayDateRange)}
            </StyledTypography>

            {showDropdownArrow && (
              <CaretDownIcon
                style={{ transform: 'rotate(90deg)', marginTop: '0.13rem', marginLeft: '0.5rem' }}
                color={dropdownArrowColor}
                size="tiniest"
              />
            )}
          </StyledButton>
        </StyledBox>
      )}
      <MeshDateRangePicker
        isOpen={open}
        minDate={minDate}
        maxDate={maxDate}
        definedRanges={predefinedRanges}
        onClose={onClose}
        onSelectDateRange={onSelectDateRange}
        selectedDateRange={displayDateRange}
        anchorEl={buttonRef.current}
        popupPlacement={popupPlacement}
        clearable={clearable}
        primaryActionText={primaryActionText}
        secondaryActionText={secondaryActionText}
        modifiers={modifiers}
        disablePortal={disablePortal}
        zIndex={zIndex}
      />
    </>
  );
};

export default DateRangeSelectionDropdown;
